import React, { useMemo, useState } from "react";

import { useAppSelector } from "../../../app/store";
import {
  EntityType,
  RelatedAs,
  RELATED_AS_LABELS,
} from "../../../api/stakeholder_relationships";
import {
  getAccountProductInfoByAccountNumber,
  getCreditAccountByAccountNumber,
  isBusinessProduct,
} from "../../creditAccounts/logic/slice";
import {
  getStakeholderEntities,
  getStakeholderRelationshipsByEntity,
} from "../logic/stakeholderSlice";

import StakeholderItem from "./StakeholderItem";
import AddStakeholderButton from "./AddStakeholderButton";
import RoleSelectionModal, { RoleOption } from "./RoleSelectionModal";
import AddStakeholderModal from "./AddStakeholderModal";

const PRIMARY_BORROWER_RELATIONSHIPS = [RelatedAs.PRIMARY_BORROWER];

const EMPTY_RELATIONSHIPS: never[] = [];

export interface KeyPersonnelSectionProps {
  accountNumber: string;
  onDirectorClick: (stakeholderId: string) => void;
}

const KEY_PERSONNEL_ROLES: RelatedAs[] = [
  RelatedAs.PROPRIETOR,
  RelatedAs.PARTNER,
  RelatedAs.DIRECTOR,
  RelatedAs.PROMOTER,
  RelatedAs.TRUSTEE,
];

const ROLE_OPTIONS: RoleOption[] = KEY_PERSONNEL_ROLES.map((role) => ({
  label: RELATED_AS_LABELS[role],
  value: role,
  description: `Key personnel related as ${RELATED_AS_LABELS[role]}`,
}));

const KeyPersonnelSection: React.FC<KeyPersonnelSectionProps> = ({
  accountNumber,
  onDirectorClick,
}) => {
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showAddStakeholderModal, setShowAddStakeholderModal] = useState(false);
  const [selectedRelatedAs, setSelectedRelatedAs] = useState<RelatedAs | null>(null);

  const account = useAppSelector((state) =>
    getCreditAccountByAccountNumber(state, accountNumber),
  );
  const displayAccountNumber = useAppSelector(
    (state) =>
      getAccountProductInfoByAccountNumber(state, accountNumber)
        ?.ext_account_number || "--",
  );

  const primaryBorrowerRelationships = useAppSelector((state) =>
    getStakeholderRelationshipsByEntity(
      state,
      EntityType.LOAN_ACCOUNT,
      accountNumber,
      PRIMARY_BORROWER_RELATIONSHIPS,
    ),
  );

  const primaryBorrowerRelationship = primaryBorrowerRelationships[0];
  const primaryBorrowerStakeholderId = primaryBorrowerRelationship?.stakeholder_id;
  const hasPrimaryBorrower = Boolean(primaryBorrowerStakeholderId);

  const relationships = useAppSelector((state) =>
    primaryBorrowerStakeholderId
      ? getStakeholderRelationshipsByEntity(
          state,
          EntityType.STAKEHOLDER,
          primaryBorrowerStakeholderId,
          KEY_PERSONNEL_ROLES,
        )
      : EMPTY_RELATIONSHIPS,
  );

  const stakeholderEntities = useAppSelector(getStakeholderEntities);

  const keyPersonnel = useMemo(
    () =>
      relationships.map((relationship) => ({
        stakeholder: stakeholderEntities[relationship.stakeholder_id],
        relatedAs: relationship.related_as,
      })),
    [relationships, stakeholderEntities],
  );

  if (!isBusinessProduct(account?.product)) {
    return null;
  }

  const handleOpenRoleModal = () => {
    if (!hasPrimaryBorrower) {
      return;
    }

    setShowRoleModal(true);
  };

  const handleRoleSelected = (selectedOption: RoleOption) => {
    setSelectedRelatedAs(selectedOption.value as RelatedAs);
    setShowRoleModal(false);
    setShowAddStakeholderModal(true);
  };

  const handleCloseAddBorrowerModal = () => {
    setShowAddStakeholderModal(false);
    setSelectedRelatedAs(null);
  };

  const handleBackToRoleModal = () => {
    setShowAddStakeholderModal(false);
    setShowRoleModal(true);
  };

  return (
    <>
      <div className="flex w-full flex-col gap-3">
        <div className="flex items-center">
          <span className="text-sm font-medium leading-5 text-[#0A0A0A]">
            Directors / Partners / Proprietors
          </span>
        </div>

        <div className="flex w-full flex-wrap items-center gap-2">
          {keyPersonnel.map(({ stakeholder, relatedAs }) => (
            <div
              key={stakeholder.stakeholder_id}
              className="w-[278px] shrink-0"
            >
              <StakeholderItem
                name={stakeholder.name}
                relatedAs={relatedAs}
                onClick={() => onDirectorClick(stakeholder.stakeholder_id)}
                className="w-full"
              />
            </div>
          ))}

          <AddStakeholderButton label="Add" onClick={handleOpenRoleModal} disabled={!hasPrimaryBorrower} />
        </div>
      </div>

      {showRoleModal && (
        <RoleSelectionModal
          title="Select Role"
          subtitle={displayAccountNumber}
          options={ROLE_OPTIONS}
          onContinue={handleRoleSelected}
          onCancel={() => setShowRoleModal(false)}
        />
      )}

      {showAddStakeholderModal &&
        selectedRelatedAs &&
        primaryBorrowerStakeholderId && (
          <AddStakeholderModal
            accountNumber={accountNumber}
            relatedAs={selectedRelatedAs}
            entityType={EntityType.STAKEHOLDER}
            entityId={primaryBorrowerStakeholderId}
            onClose={handleCloseAddBorrowerModal}
            onBack={handleBackToRoleModal}
          />
        )}
    </>
  );
};

export default KeyPersonnelSection;